import { ref, watch, type Ref } from 'vue'
import { walterBIApi } from '../../../../services/clients/AxiosClients'
import { DashboardAgilityClient } from '../../../../api/dashboard-agility/DashboardAgility.client'
import { DashboardAgilityRepository } from '../../../../api/dashboard-agility/DashboardAgility.repository'
import { SmartProcessingData } from '../../../../api/dashboard-agility/DashboardAgility.types'
import { mapDropdownKeyToInterestVariable } from '../constants/InterestVariableMap'
import {
	GeoTiffRasterRenderer,
	type RasterRender,
} from '../providers/GeoTiffRasterRenderer.provider'

class WalterBIDashboardAgilityClient extends DashboardAgilityClient {
	get(url: string, headers?: any) {
		return walterBIApi.get(url, headers)
	}
}

const buildRasterBlobUrl = (customerId: string, rasterIdentifier: string): string => {
	const baseUrl = process.env.VUE_APP_STORAGE_BASE_URL
	return `${baseUrl}/rasters-resultado-processamento/${customerId}/${rasterIdentifier}`
}

export class DashboardAgilityRasterViewModel {
	readonly farmId: Ref<string | null> = ref(null)
	readonly selectedDropdownKey: Ref<string | null> = ref(null)
	readonly currentRender: Ref<RasterRender | null> = ref(null)
	readonly isLoading: Ref<boolean> = ref(false)

	private _cache: Map<string, SmartProcessingData> = new Map()
	private _repository: DashboardAgilityRepository
	private _renderToken = 0

	constructor(repository?: DashboardAgilityRepository) {
		this._repository =
			repository ?? new DashboardAgilityRepository(new WalterBIDashboardAgilityClient())

		watch(
			[this.farmId, this.selectedDropdownKey],
			() => {
				this._refresh()
			},
			{ immediate: true },
		)
	}

	private async _refresh() {
		const token = ++this._renderToken
		const farmId = this.farmId.value
		const dropdownKey = this.selectedDropdownKey.value

		if (!farmId) {
			this.currentRender.value = null
			return
		}

		const interestVariable = mapDropdownKeyToInterestVariable(dropdownKey)
		if (!interestVariable) {
			this.currentRender.value = null
			return
		}

		let data = this._cache.get(farmId)
		if (!data) {
			this.isLoading.value = true
			try {
				data = await this._repository.getSmartProcessingDataByFarmId(farmId)
				this._cache.set(farmId, data)
			} catch (error) {
				console.error('Falha ao buscar dados de processamento smart', error)
				if (token === this._renderToken) this.currentRender.value = null
				return
			} finally {
				if (token === this._renderToken) this.isLoading.value = false
			}
		}

		if (token !== this._renderToken) return

		const bandLayerIdentifier = data.bands[interestVariable]
		if (!bandLayerIdentifier) {
			console.warn(
				`Banda "${interestVariable}" não disponível em bandas retornadas pela API. ` +
					`Bandas disponíveis: [${Object.keys(data.bands).join(', ')}]`,
			)
			this.currentRender.value = null
			return
		}

		const blobUrl = buildRasterBlobUrl(data.customerId, data.rasterIdentifier)

		try {
			const render = await GeoTiffRasterRenderer.fetchAndRender(blobUrl, bandLayerIdentifier)
			if (token === this._renderToken) this.currentRender.value = render
		} catch (error) {
			console.error('Falha ao renderizar raster', error)
			if (token === this._renderToken) this.currentRender.value = null
		}
	}
}
